// No module imports on purpose: this file must run on its own even if shared helpers fail to load.

// <s-product-recommendations> lazily fetches related/complementary products and swaps them
// into its {% partial %} region using the base theme's partial-rendering stack — the same
// Loads Shopify related products from the recommendations JSON endpoint and renders shelf cards.
// One component per file, framework-free.
//
// When the block was already rendered in the recommendations context (data-performed="true"),
// the server markup is kept and no fetch happens. Otherwise, once the block nears the
// viewport, it fetches data-url and applies the named partial, replacing the skeletons.
class ProductRecommendations extends HTMLElement {
  #observer = null;
  #controller = null;
  #resizeObserver = null;

  #onSubmitBound = (event) => this.#onSubmit(event);

  connectedCallback() {
    this.addEventListener("submit", this.#onSubmitBound);
    if (this.dataset.performed === "true") {
      this.#syncVisibility();
      return;
    }
    if (!this.dataset.url || !this.dataset.productId) return;

    // Load right away (the request is tiny); waiting for scroll proved unreliable inside previews.
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener("submit", this.#onSubmitBound);
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#controller?.abort();
  }

  // Keeps the price and Save pill on one line: when a long price (e.g. $5,654.00 + Save $1,884.00)
  // is wider than a narrow card, both shrink just enough to fit. Short prices keep full size.
  #watchPriceFit(grid) {
    const fit = () => {
      for (const row of grid.querySelectorAll("[data-price-row]")) {
        const parts = [...row.children];
        for (const part of parts) part.style.fontSize = "";
        for (let pass = 0; pass < 4 && row.scrollWidth > row.clientWidth + 1; pass++) {
          const ratio = Math.max(0.85, row.clientWidth / row.scrollWidth);
          for (const part of parts) part.style.fontSize = `${Math.max(10, parseFloat(getComputedStyle(part).fontSize) * ratio)}px`;
        }
      }
    };
    fit();
    this.#resizeObserver = new ResizeObserver(() => fit());
    this.#resizeObserver.observe(grid);
  }

  async #load() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Shopify's recommendations JSON endpoint (routes.product_recommendations_url + ".json").
    // It does not depend on which theme renders the endpoint, so it also works in theme previews.
    const url = new URL(this.dataset.url, window.location.origin);
    if (!url.pathname.endsWith(".json")) url.pathname += ".json";
    url.searchParams.set("product_id", this.dataset.productId);
    const displayLimit = Number(url.searchParams.get("limit")) || 5;
    url.searchParams.set("limit", "10");

    this.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
      const data = await response.json();
      const all = (data.products || []).filter((p) => String(p.id) !== String(this.dataset.productId));
      // "Other options" (s-other-options) shows the first 4 in-stock related products from the same
      // request; skip those here so this row shows different items.
      const shownInOptions = document.querySelector("s-other-options")
        ? new Set(all.filter((p) => p.variants?.some((v) => v.available)).slice(0, 4).map((p) => p.id))
        : new Set();
      const products = all.filter((p) => !shownInOptions.has(p.id)).slice(0, displayLimit);
      if (products.length === 0) {
        this.hidden = true;
        return;
      }
      const grid = this.#renderGrid(products);
      const slot = this.querySelector("[data-rec-skeleton]");
      if (slot) slot.replaceWith(grid);
      else return; // already filled by s-other-options
      this.hidden = false;
      this.dataset.performed = "true";
      this.#watchPriceFit(grid);
    } catch (error) {
      // Fetch failed: collapse quietly instead of showing skeletons forever.
      if (error.name !== "AbortError") {
        console.warn("[product-recommendations]", error);
        this.hidden = true;
      }
    } finally {
      this.removeAttribute("aria-busy");
      this.#controller = null;
    }
  }

  #money(cents) {
    const currency = this.dataset.currency || "AUD";
    const locale = document.documentElement.lang || "en-AU";
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol" }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Mirrors the deal card used on the homepage and collections: Deal tag with % off on the packshot,
  // title, shelf-ticket price (big price + yellow Save pill, then "Was" line) and a quick Add button.
  #renderGrid(products) {
    const list = this.#el(
      "ul",
      `grid ${this.dataset.columns || "grid-cols-2 md:grid-cols-3 xl:grid-cols-6"} gap-0 border-t border-border md:gap-3 md:border-t-0 lg:gap-4`,
    );
    list.setAttribute("role", "list");
    for (const product of products) list.append(this.#renderCard(product));
    return list;
  }

  #label(key, fallback, amount) {
    const template = this.dataset[key] || fallback;
    return amount == null ? template : template.replace("__AMOUNT__", amount);
  }

  #renderCard(product) {
    const variants = product.variants || [];
    const variant = variants.find((v) => v.available) || variants[0];
    const price = variant?.price ?? product.price;
    const compare = variant?.compare_at_price ?? product.compare_at_price;
    const onSale = compare && compare > price;
    const isRange = product.price_min != null && product.price_max != null && product.price_min !== product.price_max;

    const item = this.#el("li", "border-b border-border odd:border-e md:border-0");
    const card = this.#el(
      "div",
      "group/card relative flex h-full flex-col gap-2 rounded-none bg-surface p-3 text-on-surface md:rounded-card md:border md:border-border motion-safe:transition-shadow hover:shadow-lg",
    );

    // Packshot + Deal tag
    const media = this.#el("a", "relative block aspect-square overflow-hidden rounded-[inherit] bg-surface");
    media.href = product.url;
    media.setAttribute("aria-label", product.title);
    media.style.aspectRatio = "1 / 1";
    const src = product.featured_image || (product.images && product.images[0]);
    if (src) {
      const raw = typeof src === "string" ? src : src.src || src.url || "";
      const base = raw.startsWith("//") ? `https:${raw}` : raw;
      const img = document.createElement("img");
      img.className = "absolute inset-0 block h-full w-full object-contain";
      img.src = `${base}${base.includes("?") ? "&" : "?"}width=500`;
      img.alt = product.title;
      img.width = 500;
      img.height = 500;
      img.loading = "lazy";
      img.decoding = "async";
      media.append(img);
    }
    if (onSale) {
      const pct = Math.floor(((compare - price) * 100) / compare);
      // Round percent-off sticker, matching the Liquid product cards.
      const stickerText = pct > 0 ? (this.dataset.labelPercent || "__AMOUNT__% off").replace("__AMOUNT__", pct) : this.dataset.labelDeal || "Deal";
      const tag = this.#el(
        "span",
        "pointer-events-none absolute start-1 top-1 z-[1] flex size-12 items-center justify-center overflow-hidden rounded-full bg-sale p-1 text-center text-xs font-black uppercase leading-none text-white shadow-md ring-2 ring-white [word-spacing:100vw] md:size-14 md:text-sm",
        stickerText,
      );
      media.append(tag);
    }
    card.append(media);

    const info = this.#el("div", "flex flex-1 flex-col gap-2 pt-1");

    // Title
    const title = this.#el("h3", "type-body-sm text-on-surface line-clamp-3 min-h-[3lh]");
    const titleLink = this.#el("a", "text-inherit no-underline hover:underline", product.title);
    titleLink.href = product.url;
    title.append(titleLink);
    info.append(title);

    // Shelf-ticket price
    const ticket = this.#el("div", "flex flex-col gap-0.5");
    const row = this.#el("div", "flex min-w-0 flex-nowrap items-center gap-1.5");
    row.dataset.priceRow = "";
    const priceText = isRange ? this.#label("labelFrom", "From __AMOUNT__", this.#money(product.price_min)) : this.#money(price);
    row.append(this.#el("span", "shrink-0 whitespace-nowrap text-lg leading-none font-extrabold tracking-tight text-on-surface md:text-xl", priceText));
    if (onSale && !isRange) {
      row.append(
        this.#el(
          "span",
          "inline-flex shrink-0 items-center whitespace-nowrap rounded bg-deal px-1 py-0.5 text-[11px] leading-none font-bold text-deal-text md:text-xs",
          this.#label("labelSave", "Save __AMOUNT__", this.#money(compare - price)),
        ),
      );
    }
    ticket.append(row);
    if (onSale) ticket.append(this.#el("p", "text-xs text-on-surface-secondary", this.#label("labelWas", "Was __AMOUNT__", this.#money(compare))));
    info.append(ticket);

    // Quick Add
    const qaBase =
      "relative z-20 mt-auto inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-button max-md:rounded-full px-3 text-sm font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";
    if (variants.length > 1) {
      const choose = this.#el("a", `${qaBase} border border-border bg-surface text-on-surface no-underline hover:shadow-md`, this.dataset.labelChoose || "Choose options");
      choose.href = product.url;
      info.append(choose);
    } else if (variant?.available) {
      const form = this.#el("form", "relative z-20 mt-auto block");
      form.method = "post";
      form.action = this.dataset.cartAddUrl || "/cart/add";
      const idInput = this.#el("input");
      idInput.type = "hidden";
      idInput.name = "id";
      idInput.value = String(variant.id);
      const qty = this.#el("input");
      qty.type = "hidden";
      qty.name = "quantity";
      qty.value = "1";
      const button = this.#el("button", `${qaBase} cursor-pointer bg-deal! text-deal-text! hover:bg-deal-hover! aria-disabled:cursor-wait aria-disabled:opacity-60`);
      button.type = "submit";
      button.setAttribute("aria-label", `${this.dataset.labelAdd || "Add"}: ${product.title}`);
      const icon = this.querySelector("template[data-add-icon]");
      if (icon) {
        const iconWrap = this.#el("span", "inline-flex size-4");
        iconWrap.setAttribute("aria-hidden", "true");
        iconWrap.append(icon.content.cloneNode(true));
        button.append(iconWrap);
      }
      button.append(this.#el("span", "", this.dataset.labelAdd || "Add"));
      form.append(idInput, qty, button);
      info.append(form);
    } else {
      const sold = this.#el("span", `${qaBase} cursor-default border border-border bg-surface-secondary text-on-surface-secondary`, this.dataset.labelSoldOut || "Sold out");
      sold.setAttribute("aria-disabled", "true");
      info.append(sold);
    }

    card.append(info);
    item.append(card);
    return item;
  }

  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;
    if (!form.getAttribute("action")) return; // native /cart/add fallback

    event.preventDefault();
    const button = form.querySelector("button");
    if (button?.getAttribute("aria-disabled") === "true") return;
    button?.setAttribute("aria-disabled", "true");
    let response;
    try {
      // AJAX Cart API: writes the same session cart the Liquid drawer partials render.
      // The form action is Liquid's routes.cart_add_url; .js returns JSON.
      const variantId = Number(String(form.elements.id.value).split("/").pop());
      response = await fetch(`${form.getAttribute("action")}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        console.warn("[product-recommendations]", data?.description || data?.message);
      } else {
        const label = button?.querySelector("span:last-child");
        if (label) label.textContent = this.dataset.labelAdded || "Added";
        await customElements.get("s-cart")?.refreshMountedCarts?.();
        const dialog = document.getElementById("cart-drawer");
        if (dialog instanceof HTMLDialogElement && !dialog.open) {
          dialog.showModal();
          dialog.dispatchEvent(new CustomEvent("s-cart:opened", { bubbles: true, detail: { dialogId: dialog.id } }));
        }
      }
    } catch (error) {
      console.warn("[product-recommendations]", error);
      if (response?.ok) window.location.reload();
      else form.submit();
    } finally {
      button?.removeAttribute("aria-disabled");
    }
  }

  #syncVisibility() {
    // Server-rendered (recommendations context): hide when no product cards were rendered.
    this.hidden = !this.querySelector("a[href]");
  }
}

if (!customElements.get("s-product-recommendations")) {
  customElements.define("s-product-recommendations", ProductRecommendations);
}
